import { Controller } from "@hotwired/stimulus"
import {
  providerMatchesFilters,
  visibleProviderIndexes,
} from "../lib/provider_search_filters.mjs"

const PLAN_ACCEPTANCE_HIDDEN_APPEARANCES = ["yellow", "red"]

function planAcceptanceFilterActive(hiddenAppearances) {
  return PLAN_ACCEPTANCE_HIDDEN_APPEARANCES.every((appearance) => hiddenAppearances.includes(appearance))
}

// Global-admin preview controls for provider-search result visibility.
// Quick chips (Active / Plan acceptance) sit above the results; the detailed
// controls live in a bottom sheet that slides up over the results.
export default class extends Controller {
  static targets = [
    "activeCheckbox",
    "activeChip",
    "appearanceCheckbox",
    "feedbackWidget",
    "hiddenCount",
    "hiddenNotice",
    "planAcceptanceChip",
    "providerCard",
    "sheet",
    "sheetOverlay",
    "sheetTrigger",
  ]

  static values = {
    activeByDefault: Boolean,
    hiddenAppearancesByDefault: Array,
    providers: Array,
  }

  connect() {
    this.activeOnly = this.activeByDefaultValue
    this.hiddenAppearances = [...this.hiddenAppearancesByDefaultValue]
    this.closeTimeout = null
    this.keydownHandler = (event) => {
      if (event.key === "Escape" && this.sheetOpen()) this.closeSheet()
    }
    document.addEventListener("keydown", this.keydownHandler)
    this.syncInputs()
    this.applyFilters()
    this.initialized = true
  }

  // Lazily loaded result chunks connect their cards after the initial render;
  // re-apply the current filters so they arrive with the right visibility.
  providerCardTargetConnected() {
    if (!this.initialized) return
    this.applyFilters()
  }

  disconnect() {
    document.removeEventListener("keydown", this.keydownHandler)
    if (this.closeTimeout) clearTimeout(this.closeTimeout)
  }

  toggleActive(event) {
    event?.preventDefault?.()
    this.activeOnly = !this.activeOnly
    this.syncInputs()
    this.applyFilters()
  }

  togglePlanAcceptance(event) {
    event?.preventDefault?.()
    this.hiddenAppearances = planAcceptanceFilterActive(this.hiddenAppearances)
      ? this.hiddenAppearances.filter((appearance) => !PLAN_ACCEPTANCE_HIDDEN_APPEARANCES.includes(appearance))
      : [...new Set([...this.hiddenAppearances, ...PLAN_ACCEPTANCE_HIDDEN_APPEARANCES])]
    this.syncInputs()
    this.applyFilters()
  }

  updateFromCheckboxes() {
    if (this.hasActiveCheckboxTarget) this.activeOnly = this.activeCheckboxTarget.checked
    this.hiddenAppearances = this.appearanceCheckboxTargets
      .filter((checkbox) => !checkbox.checked)
      .map((checkbox) => checkbox.value)
    this.syncInputs()
    this.applyFilters()
  }

  // Clears every filter from the compact "Show N hidden" action.
  showAll(event) {
    event?.preventDefault?.()
    this.activeOnly = false
    this.hiddenAppearances = []
    this.syncInputs()
    this.applyFilters()
  }

  openSheet(event) {
    event?.preventDefault?.()
    if (this.closeTimeout) clearTimeout(this.closeTimeout)
    this.closeTimeout = null
    this.previouslyFocused = event?.currentTarget || document.activeElement
    this.sheetTriggerTarget.setAttribute("aria-expanded", "true")
    this.sheetOverlayTarget.classList.remove("hidden")
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.sheetTarget.classList.remove("translate-y-full")
        // preventScroll: the sheet is still transformed off the bottom edge for the
        // 300ms slide, so a scrolling focus drags the whole drawer body down with it.
        this.sheetTarget.querySelector("button, input, select, textarea, [href], [tabindex]:not([tabindex='-1'])")?.focus({ preventScroll: true })
      })
    })
  }

  closeSheet(event) {
    event?.preventDefault?.()
    this.sheetTarget.classList.add("translate-y-full")
    this.sheetTriggerTarget.setAttribute("aria-expanded", "false")
    if (this.closeTimeout) clearTimeout(this.closeTimeout)
    this.closeTimeout = setTimeout(() => {
      this.sheetOverlayTarget.classList.add("hidden")
      this.closeTimeout = null
    }, 300)
    if (this.previouslyFocused?.focus) this.previouslyFocused.focus({ preventScroll: true })
    this.previouslyFocused = null
  }

  sheetOpen() {
    return this.hasSheetOverlayTarget && !this.sheetOverlayTarget.classList.contains("hidden")
  }

  applyFilters() {
    const filters = { activeOnly: this.activeOnly, hiddenAppearances: this.hiddenAppearances }
    let visibleIndex = 0
    const visibleCards = []

    this.providerCardTargets.forEach((card) => {
      const visible = providerMatchesFilters(card.dataset, filters)
      card.classList.toggle("hidden", !visible)
      card.setAttribute("aria-hidden", String(!visible))

      if (visible) {
        visibleIndex += 1
        visibleCards.push(card)
        const number = card.querySelector("[data-provider-result-number]")
        if (number) number.textContent = String(visibleIndex)
      }
    })

    if (this.hasFeedbackWidgetTarget) {
      const feedbackAnchor = visibleCards[Math.min(visibleCards.length, 2) - 1]
      this.feedbackWidgetTarget.classList.toggle("hidden", !feedbackAnchor)
      this.feedbackWidgetTarget.setAttribute("aria-hidden", String(!feedbackAnchor))
      feedbackAnchor?.after(this.feedbackWidgetTarget)
    }

    // Prefer the full result-set metadata (which includes providers whose
    // cards have not been lazily loaded yet) so map markers stay in sync.
    const providerData = this.providersValue?.length > 0
      ? this.providersValue
      : this.providerCardTargets.map((card) => card.dataset)
    const visibleIndexes = visibleProviderIndexes(providerData, filters)
    const hiddenCount = providerData.length - visibleIndexes.length
    if (this.hasHiddenNoticeTarget) {
      this.hiddenNoticeTarget.classList.toggle("hidden", hiddenCount === 0)
      this.hiddenNoticeTarget.setAttribute("aria-label", `Show ${hiddenCount} hidden ${hiddenCount === 1 ? "provider" : "providers"} by clearing filters`)
      if (this.hasHiddenCountTarget) this.hiddenCountTarget.textContent = String(hiddenCount)
    }
    this.dispatch("updated", {
      detail: {
        visibleProviderIndexes: visibleIndexes,
      },
    })
  }

  syncInputs() {
    if (this.hasActiveCheckboxTarget) this.activeCheckboxTarget.checked = this.activeOnly
    this.appearanceCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = !this.hiddenAppearances.includes(checkbox.value)
    })
    if (this.hasActiveChipTarget) {
      this.activeChipTarget.setAttribute("aria-pressed", String(this.activeOnly))
    }
    if (this.hasPlanAcceptanceChipTarget) {
      this.planAcceptanceChipTarget.setAttribute("aria-pressed", String(planAcceptanceFilterActive(this.hiddenAppearances)))
    }
  }
}
